/**
 * Page de liste des enfants avec gestion complète (CRUD).
 * Pour les admins : recherche obligatoire, rien ne s'affiche par défaut.
 * Pour les parents : affiche leurs enfants normalement.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { enfantService, getMediaUrl } from '../services/api';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { notify } from '../components/NotificationContainer';
import { confirm } from '../components/ConfirmContainer';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const EnfantsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [enfants, setEnfants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEnfant, setEditingEnfant] = useState(null);
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    date_naissance: '',
    lieu_naissance: '',
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  // États pour la recherche admin
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const isAdmin = user?.role === 'ADMIN';

  // Récupérer les paramètres de redirection
  const returnTo = searchParams.get('returnTo');
  const sejourId = searchParams.get('sejourId');

  // Charger les enfants au montage (seulement pour les parents)
  useEffect(() => {
    if (!isAdmin) {
      loadEnfants();
    }
    // Ouvrir automatiquement le modal si on vient d'une page de séjour
    if (returnTo === 'sejour' && sejourId) {
      setShowModal(true);
    }
  }, [returnTo, sejourId, isAdmin]);

  // Recherche avec debounce pour les admins
  useEffect(() => {
    if (!isAdmin) return;

    const delaySearch = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery, isAdmin]);

  const loadEnfants = async () => {
    try {
      setLoading(true);
      const response = await enfantService.getAll();
      setEnfants(response.data.results || response.data);
    } catch (error) {
      logger.error('Erreur lors du chargement des enfants:', error);
      notify.error('Erreur lors du chargement des enfants');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query || query.trim().length === 0) {
      // Si pas de recherche, vider la liste pour les admins
      setEnfants([]);
      setHasSearched(false);
      return;
    }

    // Attendre au moins 3 caractères avant de lancer la recherche
    if (query.trim().length < 3) {
      setEnfants([]);
      setHasSearched(false);
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      const response = await enfantService.search(query.trim());
      setEnfants(response.data.results || response.data);
    } catch (error) {
      logger.error('Erreur lors de la recherche:', error);
      notify.error('Erreur lors de la recherche des enfants');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (enfant = null) => {
    if (enfant) {
      setEditingEnfant(enfant);
      setFormData({
        prenom: enfant.prenom,
        nom: enfant.nom,
        date_naissance: enfant.date_naissance,
        lieu_naissance: enfant.lieu_naissance || '',
        photo: null,
      });
      // Utiliser l'URL de l'endpoint protégé si disponible, sinon fallback
      setPhotoPreview(enfant.photo_url || (enfant.photo ? getMediaUrl(enfant.photo) : null));
    } else {
      setEditingEnfant(null);
      setFormData({
        prenom: '',
        nom: '',
        date_naissance: '',
        lieu_naissance: '',
        photo: null,
      });
      setPhotoPreview(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEnfant(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('prenom', formData.prenom);
      formDataToSend.append('nom', formData.nom);
      formDataToSend.append('date_naissance', formData.date_naissance);
      formDataToSend.append('lieu_naissance', formData.lieu_naissance);

      if (formData.photo) {
        formDataToSend.append('photo', formData.photo);
      }

      logger.log('=== Envoi des données enfant ===');
      logger.log('Prénom:', formData.prenom);
      logger.log('Nom:', formData.nom);
      logger.log('Date naissance:', formData.date_naissance);
      logger.log('Lieu naissance:', formData.lieu_naissance);
      logger.log('Photo:', formData.photo);

      if (editingEnfant) {
        logger.log('Mise à jour enfant ID:', editingEnfant.id);
        await enfantService.update(editingEnfant.id, formDataToSend);
      } else {
        logger.log('Création nouvel enfant');
        await enfantService.create(formDataToSend);
      }

      // Recharger selon le rôle
      if (isAdmin) {
        // Pour les admins, recharger la recherche si elle était active
        if (searchQuery) {
          await handleSearch(searchQuery);
        }
        // Sinon ne rien faire (pas de chargement par défaut)
      } else {
        // Pour les parents, toujours recharger
        await loadEnfants();
      }

      handleCloseModal();

      // Si on vient d'une page de séjour, rediriger vers cette page
      if (returnTo === 'sejour' && sejourId) {
        navigate(`/sejours?openModal=${sejourId}`);
      }
    } catch (error) {
      logger.error('=== ERREUR lors de l\'enregistrement ===');
      logger.error('Erreur complète:', error);
      logger.error('Réponse:', error.response);
      logger.error('Data:', error.response?.data);

      const errorMsg = error.response?.data?.detail ||
                       error.response?.data?.message ||
                       JSON.stringify(error.response?.data) ||
                       'Erreur lors de l\'enregistrement de l\'enfant';
      notify.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (await confirm.delete('Êtes-vous sûr de vouloir supprimer cet enfant ?', 'Supprimer l\'enfant')) {
      try {
        await enfantService.delete(id);

        // Recharger selon le rôle
        if (isAdmin) {
          // Pour les admins, recharger la recherche si elle était active
          if (searchQuery) {
            await handleSearch(searchQuery);
          }
          // Sinon ne rien faire (pas de chargement par défaut)
        } else {
          // Pour les parents, toujours recharger
          await loadEnfants();
        }

        notify.success('Enfant supprimé avec succès');
      } catch (error) {
        logger.error('Erreur lors de la suppression:', error);
        notify.error('Erreur lors de la suppression de l\'enfant');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        photo: file,
      });
      // Créer un aperçu de la photo
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Mes enfants
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Gérez les fiches de vos enfants pour les inscrire aux séjours
          </p>
        </div>

        {/* Champ de recherche pour les admins */}
        {isAdmin && (
          <div className="mb-6 bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher un enfant (optionnel)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tapez le nom ou le prénom de l'enfant..."
                className="block w-full pl-10 pr-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>
            {searchQuery && (
              <p className="mt-2 text-xs text-gray-500">
                {searchQuery.trim().length < 3 ? (
                  <span className="text-amber-600">
                    Tapez au moins 3 caractères pour lancer la recherche
                  </span>
                ) : loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full"></span>
                    Recherche en cours...
                  </span>
                ) : hasSearched ? (
                  `${enfants.length} résultat(s) trouvé(s)`
                ) : null}
              </p>
            )}
          </div>
        )}

        {/* Message pour admin sans recherche */}
        {isAdmin && !hasSearched ? (
          <div className="bg-gray-50 rounded-lg p-8 sm:p-12 text-center border border-gray-200">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlassIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Recherchez un enfant ou ajoutez-en un</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto mb-6">
              Utilisez la barre de recherche ci-dessus pour trouver un enfant existant, ou cliquez sur le bouton ci-dessous pour ajouter un nouvel enfant.
            </p>
            <button
              onClick={() => handleOpenModal()}
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white py-2 px-5 rounded-md text-sm font-medium transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Ajouter un enfant
            </button>
          </div>
        ) : hasSearched && enfants.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center text-3xl sm:text-4xl mx-auto mb-4">
              🔍
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Aucun résultat trouvé</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto">
              Aucun enfant ne correspond à votre recherche "<strong>{searchQuery}</strong>"
            </p>
          </div>
        ) : enfants.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center text-3xl sm:text-4xl mx-auto mb-4">
              👶
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Aucun enfant enregistré</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6 max-w-md mx-auto">
              Commencez par ajouter la fiche de votre enfant pour pouvoir l'inscrire à nos séjours
            </p>
            <button
              onClick={() => handleOpenModal()}
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white py-2 px-5 rounded-md text-sm font-medium transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Ajouter mon premier enfant
            </button>
          </div>
        ) : (
          <>
            {/* Bouton ajouter */}
            <div className="mb-4 sm:mb-6 flex justify-end">
              <button
                onClick={() => handleOpenModal()}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
              >
                <PlusIcon className="h-5 w-5" />
                Ajouter un enfant
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {enfants.map((enfant) => (
                <div
                  key={enfant.id}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden border border-gray-200"
                >
                  <div className="bg-gray-50 border-b border-gray-200 p-4 sm:p-5">
                    <div className="flex items-start justify-between mb-3">
                      {enfant.photo ? (
                        <img
                          src={getMediaUrl(enfant.photo)}
                          alt={`${enfant.prenom} ${enfant.nom}`}
                          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white border border-gray-200 rounded-full flex items-center justify-center text-2xl flex-shrink-0">
                          {enfant.age < 6 ? '👶' : enfant.age < 12 ? '🧒' : '👦'}
                        </div>
                      )}
                      <div className="text-right">
                        <div className="text-xl sm:text-2xl font-bold text-gray-900">{enfant.age}</div>
                        <div className="text-xs sm:text-sm text-gray-500">ans</div>
                      </div>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                      {enfant.prenom} {enfant.nom}
                    </h3>
                  </div>

                  <div className="p-4 sm:p-5">
                    <div className="space-y-2.5 text-xs sm:text-sm">
                      <div className="flex items-start text-gray-600">
                        <svg className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="break-words">Né(e) le {new Date(enfant.date_naissance).toLocaleDateString('fr-FR')}</span>
                      </div>
                      {enfant.lieu_naissance && (
                        <div className="flex items-start text-gray-600">
                          <svg className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="break-words">{enfant.lieu_naissance}</span>
                        </div>
                      )}
                      {/* Infos parent pour les admins */}
                      {isAdmin && enfant.parent_details && (
                        <div className="flex items-start text-gray-600">
                          <svg className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="break-words">
                            Parent: {enfant.parent_details.prenom} {enfant.parent_details.nom}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Boutons d'action */}
                    <div className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleOpenModal(enfant)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm text-primary-700 hover:bg-primary-50 active:bg-primary-100 rounded-md transition-colors font-medium"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(enfant.id)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 active:bg-red-100 rounded-md transition-colors font-medium"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </>
      )}

      {/* Modal de création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start sm:items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-xl sm:rounded-2xl shadow-2xl transform transition-all my-4 sm:my-8">
            {/* En-tête coloré */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-xl sm:text-2xl">
                    {editingEnfant ? '✏️' : '👶'}
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-white">
                    {editingEnfant ? 'Modifier l\'enfant' : 'Ajouter un enfant'}
                  </h3>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition-colors -mr-1"
                >
                  <span className="text-2xl leading-none">&times;</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="prenom"
                    required
                    value={formData.prenom}
                    onChange={handleChange}
                    className="block w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    placeholder="Ex: Jean"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    required
                    value={formData.nom}
                    onChange={handleChange}
                    className="block w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    placeholder="Ex: Dupont"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Date de naissance <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date_naissance"
                    required
                    value={formData.date_naissance}
                    onChange={handleChange}
                    className="block w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Lieu de naissance <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lieu_naissance"
                    required
                    value={formData.lieu_naissance}
                    onChange={handleChange}
                    className="block w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    placeholder="Ex: Paris, France"
                  />
                </div>
              </div>

              {/* Champ Photo */}
              <div className="mt-4 sm:mt-5">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Photo de l'enfant
                  <span className="text-xs font-normal text-gray-500 ml-2 block sm:inline mt-1 sm:mt-0">(optionnelle, peut être ajoutée lors de l'inscription au séjour)</span>
                </label>
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  {photoPreview && (
                    <div className="flex-shrink-0 self-start">
                      <img
                        src={photoPreview}
                        alt="Aperçu"
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover border-2 border-gray-200"
                      />
                    </div>
                  )}
                  <div className="flex-grow min-w-0">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="block w-full text-xs sm:text-sm text-gray-500
                        file:mr-3 sm:file:mr-4 file:py-2 sm:file:py-2.5 file:px-3 sm:file:px-4
                        file:rounded-lg file:border-0
                        file:text-xs sm:file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100
                        transition-all"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Format accepté : JPG, PNG, JPEG. Taille max : 5 Mo
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 active:from-blue-700 active:to-indigo-800 rounded-lg text-xs sm:text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all"
                >
                  {editingEnfant ? '✓ Mettre à jour' : '✓ Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default EnfantsList;
